import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { CalendarDays, CheckCircle2, Plus, Rocket } from "lucide-react";
import ControlLink from "./ControlLink";
import LinkExistingStep from "./LinkExistingStep";
import RequirementCard from "./RequirementCard";
import { DepartmentBadge, StatePill } from "./ProgressParts";
import { BlockedBadge, MemberAvatar } from "./WorkCardMeta";
import { useOverlays } from "./overlay-context";
import { useMembers } from "../wiki/use-member-names";
import { useSession } from "../session-context";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { apiFetch, v1Path } from "../../lib/api";
import { formatDateOnly, formatTimestampDate } from "../../lib/dates";
import {
  canMarkLaunchLive,
  isStepDone,
  launchCountdown,
  launchSteps,
  setLaunchLive,
  sortLaunchSteps,
  stepProgress,
  type LaunchStepRow,
} from "../../lib/launch";
import { addInitiativeRequirement, fetchInitiativeTemplate, inTemplateOrder } from "../../lib/initiative-requirements";
import { fetchInitiativeWorkspace, isRequirementKey } from "../../lib/initiatives";
import { progressPercent } from "../../lib/progress";
import { cn } from "../../lib/utils";
import type {
  MemberDto,
  ProjectDto,
  StateDto,
  WorkItemFull,
} from "../../lib/work-types";

type Layout = "order" | "stream";

const LAYOUT_KEY = "bb:launch-plan-layout";

function changed() {
  window.dispatchEvent(new CustomEvent("bb:work-items-changed"));
}

function GoLiveDate({
  title,
  dueDate,
  live,
  readOnly,
  onChange,
}: {
  title: string;
  dueDate: string | null;
  live: boolean;
  readOnly: boolean;
  onChange: (value: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const countdown = launchCountdown(dueDate, live, new Date());
  const overdue = !live && countdown.endsWith("overdue");
  const label = dueDate ? (formatDateOnly(dueDate) ?? dueDate) : "Not set";

  const open = () => {
    const el = input.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  };

  return (
    <div className="relative shrink-0 sm:w-36 sm:border-l sm:pl-4">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Go-live
      </p>
      <button
        type="button"
        disabled={readOnly}
        onClick={open}
        aria-label={`${title} go-live date: ${label}`}
        className={cn(
          "mt-0.5 flex items-center gap-1.5 rounded-md text-sm font-semibold",
          !readOnly && "hover:text-primary",
          !dueDate && "text-muted-foreground",
        )}
      >
        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        {label}
      </button>
      <p
        className={cn(
          "mt-0.5 text-[11px] text-muted-foreground",
          overdue && "text-red-600",
          live && "text-emerald-700",
        )}
      >
        {dueDate ? countdown : readOnly ? "No go-live date" : "Click to set"}
      </p>
      <input
        ref={input}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={dueDate ?? ""}
        disabled={readOnly}
        onChange={(event) => onChange(event.target.value || null)}
        className="pointer-events-none absolute bottom-0 left-4 h-0 w-0 opacity-0"
      />
    </div>
  );
}

function StepRow({
  row,
  number,
  state,
  streamName,
  members,
  initiativeId,
  canGoLive,
  readOnly,
  canGoLiveWhenReadOnly,
}: {
  row: LaunchStepRow;
  number: number | null;
  state: StateDto | undefined;
  streamName: string | null;
  members: Map<string, MemberDto>;
  initiativeId: string;
  canGoLive: boolean;
  readOnly: boolean;
  canGoLiveWhenReadOnly: boolean;
}) {
  const { toast } = useOverlays();
  const live = Boolean(row.live_at);
  const progress = stepProgress(row, state);
  const percent = live ? 100 : progressPercent(progress);
  const done = isStepDone(state);
  const owners = row.assignee_ids
    .map((id) => members.get(id))
    .filter((member): member is MemberDto => Boolean(member));

  async function patchDates(fields: Record<string, unknown>) {
    try {
      await apiFetch(
        v1Path(`/projects/${row.project_id}/work-items/${row.id}`),
        {
          method: "PATCH",
          body: JSON.stringify({ version: row.version, ...fields }),
        },
      );
      changed();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not change the date");
    }
  }

  async function toggleLive() {
    try {
      await setLaunchLive(initiativeId, row.id, !live);
      toast(
        live ? `${row.title} is no longer marked live` : `${row.title} is live`,
      );
      changed();
    } catch (err) {
      toast(
        err instanceof Error ? err.message : "Could not update the launch step",
      );
    }
  }

  const title = (
    <ControlLink
      workItemId={row.id}
      workItemKey={row.key}
      projectId={row.project_id}
      className="truncate text-sm font-medium text-foreground no-underline hover:underline"
    >
      {row.title}
    </ControlLink>
  );

  return (
    <li
      className={cn(
        "flex flex-col gap-3 p-3 sm:flex-row sm:items-center",
        live && "bg-emerald-50/60 dark:bg-emerald-950/20",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              live
                ? "bg-emerald-500 text-white"
                : "bg-muted text-muted-foreground",
            )}
            aria-hidden
          >
            {live ? <CheckCircle2 className="h-4 w-4" /> : (number ?? "–")}
          </span>
          <div className="flex min-w-[12rem] flex-1 items-center gap-2">
            {title}
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {row.key}
            </span>
          </div>
          {live ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100">
              <Rocket className="h-3 w-3" aria-hidden />
              Live {row.live_at ? formatTimestampDate(row.live_at) : ""}
            </span>
          ) : done ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900 dark:text-amber-100">
              Ready to go live
            </span>
          ) : null}
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-28 overflow-hidden rounded-full bg-muted"
              aria-hidden
            >
              <div
                className={cn(
                  "h-full rounded-full",
                  percent === 100 ? "bg-emerald-500" : "bg-primary",
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
              {progress.done} / {progress.total}
            </span>
          </div>
          {canGoLive && (!readOnly || canGoLiveWhenReadOnly) ? (
            <Button
              type="button"
              size="sm"
              variant={live ? "ghost" : "outline"}
              onClick={() => void toggleLive()}
            >
              {live ? "Undo live" : "Mark live"}
            </Button>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 pl-10 text-xs text-muted-foreground">
          {streamName ? (
            <span className="inline-flex items-center gap-1.5">
              <DepartmentBadge projectId={row.project_id} name={streamName} />
              {streamName}
            </span>
          ) : null}
          {state ? (
            <StatePill
              name={state.name}
              group={state.group}
              className="px-1.5 py-0 text-[11px]"
            />
          ) : null}
          <BlockedBadge reason={row.blocked_reason} className="max-w-xs" />
          {owners.length > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="flex -space-x-1.5">
                {owners.slice(0, 3).map((member) => (
                  <MemberAvatar
                    key={member.user_id}
                    member={member}
                    className="ring-2 ring-card"
                  />
                ))}
              </span>
            </span>
          ) : (
            <span>Unassigned</span>
          )}
        </div>
      </div>
      <GoLiveDate
        title={row.title}
        dueDate={row.due_date}
        live={live}
        readOnly={readOnly}
        onChange={(value) => void patchDates({ due_date: value })}
      />
    </li>
  );
}

/** A requirement for this initiative only; the org template in settings is unchanged. */
function AddRequirement({ initiativeId }: { initiativeId: string }) {
  const { toast } = useOverlays();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  function close() {
    setOpen(false);
    setTitle("");
    setDescription("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const created = await addInitiativeRequirement(initiativeId, {
        title: trimmed,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      toast(`Added ${created.key}`);
      changed();
      close();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not add the requirement");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" aria-hidden />
        Add requirement
      </Button>
    );
  }
  return (
    <form onSubmit={submit} className="mt-2 space-y-3 rounded-lg border p-3" aria-label="Add requirement">
      <div className="space-y-1.5">
        <Label htmlFor="new-requirement-title">Requirement</Label>
        <Input
          id="new-requirement-title"
          autoFocus
          maxLength={200}
          value={title}
          placeholder="e.g. Brand"
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new-requirement-description">What it needs (optional)</Label>
        <Textarea
          id="new-requirement-description"
          rows={3}
          maxLength={5000}
          value={description}
          placeholder="e.g. The dashboards and reporting a brand user sees"
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <p className="text-xs text-muted-foreground">Only this initiative gets it. The default list in settings stays the same.</p>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={busy || !title.trim()}>
          Add requirement
        </Button>
      </div>
    </form>
  );
}

function AddStep({ initiativeId, initiativeKey }: { initiativeId: string; initiativeKey: string }) {
  const { openCreateWorkItem } = useOverlays();
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => openCreateWorkItem({ launchStep: { initiativeId } })}
      >
        <Plus className="h-4 w-4" aria-hidden />
        Add launch step
      </Button>
      <LinkExistingStep initiativeId={initiativeId} initiativeKey={initiativeKey} />
    </div>
  );
}

/**
 * One list of what this initiative launches. Each step is a real task, so its dates and
 * status are the task's own; Live is set by its stream, separately from done.
 */
export default function LaunchPlan({
  initiative,
  tasks,
  statesById,
  loading,
  readOnly,
  streamView,
  summary,
}: {
  initiative: WorkItemFull;
  tasks: LaunchStepRow[];
  statesById: Map<string, StateDto>;
  loading: boolean;
  readOnly: boolean;
  streamView: ReactNode;
  summary?: ReactNode;
}) {
  const session = useSession();
  const members = useMembers();
  const [layout, setLayout] = useState<Layout>(() =>
    window.localStorage.getItem(LAYOUT_KEY) === "stream" ? "stream" : "order",
  );
  const steps = useMemo(
    () => sortLaunchSteps(launchSteps(tasks, initiative.id)),
    [initiative.id, tasks],
  );
  const [templateTitles, setTemplateTitles] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchInitiativeTemplate()
      .then((template) => !cancelled && setTemplateTitles(template.requirements.map((entry) => entry.title)))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const allRequirementSteps = inTemplateOrder(
    steps.filter((row) => isRequirementKey(row.key)),
    templateTitles,
  );
  // Removed requirements sit in Cancelled; their data is kept, but they are not shown.
  const isRemoved = (row: LaunchStepRow) => statesById.get(row.state_id)?.group === "cancelled";
  const requirementSteps = allRequirementSteps.filter((row) => !isRemoved(row));
  const streamSteps = steps.filter((row) => !isRequirementKey(row.key));
  const dated = streamSteps.filter((row) => row.due_date);
  const undated = streamSteps.filter((row) => !row.due_date);
  const liveCount = streamSteps.filter((row) => row.live_at).length;
  const memberList = useMemo(() => Array.from(members.values()), [members]);
  const [workspaceStates, setWorkspaceStates] = useState<StateDto[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchInitiativeWorkspace()
      .then((workspace) => !cancelled && setWorkspaceStates(workspace.requirements.states))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const canGoLive = canMarkLaunchLive(session);
  // Everyone sees every step; only a step's stream members change it (org admins may also mark it live).
  const [myStreamIds, setMyStreamIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ items: ProjectDto[] }>(v1Path("/projects?limit=100&offset=0"))
      .then((res) => {
        if (!cancelled) setMyStreamIds(new Set(res.items.filter((p) => p.is_member !== false).map((p) => p.id)));
      })
      .catch(() => !cancelled && setMyStreamIds(new Set()));
    return () => {
      cancelled = true;
    };
  }, []);
  const inStream = (projectId: string) => myStreamIds?.has(projectId) ?? false;
  const streamNames = useMemo(
    () =>
      new Map(
        (initiative.initiative_rollup?.departments ?? []).map((d) => [
          d.project_id,
          d.project_name,
        ]),
      ),
    [initiative.initiative_rollup],
  );

  const choose = (next: Layout) => {
    setLayout(next);
    window.localStorage.setItem(LAYOUT_KEY, next);
  };

  const rowFor = (row: LaunchStepRow, number: number | null) => (
    <StepRow
      key={row.id}
      row={row}
      number={number}
      state={statesById.get(row.state_id)}
      streamName={streamNames.get(row.project_id) ?? row.key.replace(/-\d+$/, "")}
      members={members}
      initiativeId={initiative.id}
      canGoLive={canGoLive && (inStream(row.project_id) || session.isOrgAdmin)}
      readOnly={readOnly || !inStream(row.project_id)}
      canGoLiveWhenReadOnly={!readOnly && session.isOrgAdmin}
    />
  );

  return (
    <section className="rounded-xl border bg-card p-4 text-card-foreground">
      {summary ? <div className="mb-5 border-b pb-5">{summary}</div> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Launch plan</h2>
        <div className="flex items-center gap-2">
          {streamSteps.length > 0 ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
              {liveCount} of {streamSteps.length} live
            </span>
          ) : null}
          <div
            className="inline-flex rounded-md border p-0.5 text-xs"
            role="group"
            aria-label="Group launch plan by"
          >
            {(["order", "stream"] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={layout === option}
                onClick={() => choose(option)}
                className={cn(
                  "rounded px-2 py-1 font-medium",
                  layout === option
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option === "order" ? "Launch order" : "Stream"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {layout === "stream" ? (
        <div className="mt-4">{streamView}</div>
      ) : loading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Business requirements
            </h3>
            {requirementSteps.length > 0 ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                {requirementSteps.filter((row) => isStepDone(statesById.get(row.state_id))).length} of{" "}
                {requirementSteps.length} done
              </span>
            ) : null}
          </div>
          {requirementSteps.length > 0 ? (
            <ul className="mt-2 divide-y overflow-hidden rounded-lg border">
              {requirementSteps.map((row, index) => (
                <RequirementCard
                  key={row.id}
                  row={row}
                  number={index + 1}
                  state={statesById.get(row.state_id)}
                  states={workspaceStates}
                  members={memberList}
                  readOnly={readOnly}
                />
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">No business requirements yet.</p>
          )}
          {readOnly ? null : <AddRequirement initiativeId={initiative.id} />}

          <h3 className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Streams &amp; additional tasks
          </h3>
          {streamSteps.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing from a stream yet. Add a step below, or link a task that already exists.
            </p>
          ) : null}
          {dated.length > 0 ? (
            <ol className="divide-y overflow-hidden rounded-lg border">
              {dated.map((row, index) => rowFor(row, index + 1))}
            </ol>
          ) : null}
          {undated.length > 0 ? (
            <>
              <p className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                No go-live date · {undated.length}
              </p>
              <ul className="divide-y overflow-hidden rounded-lg border">
                {undated.map((row) => rowFor(row, null))}
              </ul>
            </>
          ) : null}
        </>
      )}
      {!readOnly && layout === "order" ? (
        <AddStep initiativeId={initiative.id} initiativeKey={initiative.key} />
      ) : null}
    </section>
  );
}
